/* eslint-disable max-lines */
import {
    Button,
    HStack,
    IconButton,
    Input,
    Popover,
    PopoverArrow,
    PopoverBody,
    PopoverContent,
    PopoverTrigger,
    Text,
    useDisclosure,
    VStack,
} from '@chakra-ui/react';
import React, { useEffect, useMemo, useState } from 'react';
import {
    DayPicker,
    DayPickerSingleProps,
    CaptionProps,
    useNavigation,
} from 'react-day-picker';
import {
    format, isAfter, isBefore, isMatch, isSameDay, isSameMonth, parse,
    startOfDay, parseISO, isValid,
} from 'date-fns';
import { faAngleLeft, faAngleRight } from '@fortawesome/pro-regular-svg-icons';
import { DateInput } from './date-input';
import { FaIcon } from '../fa-icon';

type BaseDayPickerProps = Omit<DayPickerSingleProps, 'selected' | 'onSelect' | 'mode'>;

class InvalidDateInput {}
// Reuse one sentinel instance to avoid triggering parent re-renders
// on each keystroke while the value is still invalid
const INVALID_DATE_SENTINEL = new InvalidDateInput();

export interface DatePickerWithInputProps extends BaseDayPickerProps {
    /**
   * Size of the input element
   *
   * Defaults to "md"
   */
    size?: React.ComponentProps<typeof DateInput>['size'];

    /**
   * Placeholder text to show if there is no dates selected
   *
   * Defaults to ""
   */
    placeholder?: React.ComponentProps<typeof DateInput>['placeholder'];

    /**
   * What to display when no date is selected
   *
   * Defaults to show the placeholder instead
   */
    noDateSelectedText?: string;

    /**
   * Controlled Date
   */
    // Temporarily allow string + InvalidDateInput while migrating upstream to string-only dates
    value?: Date | string | null | InvalidDateInput;

    /**
   * Callback fired after the date range has changed, will return ISO Dates
   */
    onChange?: (newDate: Date | string | null | InvalidDateInput) => void;

    /**
   * Callback fired after the Date Range Picker popover has closed
   */
    onBlur?: () => void;

    /**
   * Show the input in an error state
   */
    isInvalid?: boolean;

    'aria-label'?: string;

    /**
   * Allow the date to be cleared after having been selected
   *
   * Defaults to true
   */
    allowClear?: boolean;

    disabled?: boolean;

    /**
   * Whether the popover should close when a date is selected
   *
   * Defaults to true
   */
    closeWhenSelectDate?: boolean;

    /**
   * Show a button within the popover that will set the date to null (no date)
   *
   * Defaults to false
   */
    showNoDateButton?: boolean;

    /**
     * Show a button within the popover that will set the date to today
     *
     * Defaults to false
     */
    showTodayButton?: boolean;

    /**
   * Change the text of the no date button
   *
   * Defaults to "No date"
   */
    noDateButtonText?: string;

    /**
     * Props to pass to the input element
     */
    inputProps?: React.ComponentProps<typeof Input>;
}

const NAV_BUTTON_PROPS = {
    p: 0,
    borderRadius: 'full',
    colorScheme: 'gray',
    h: 7,
    w: 7,
    minW: 'unset',
    variant: 'ghost',
};

function DatePickerCaption({ displayMonth }: CaptionProps) {
    const {
        goToMonth, nextMonth, previousMonth, currentMonth, displayMonths,
    } = useNavigation();

    const displayPreviousMonth = useMemo(
        () => isSameMonth(displayMonth, displayMonths[0]),
        [displayMonths, currentMonth]
    );

    const displayNextMonth = useMemo(
        () => isSameMonth(displayMonth, displayMonths[displayMonths.length - 1]),
        [displayMonths, currentMonth]
    );

    return (
        <HStack pt={1} pb={1} gap={0} justifyContent="space-between">
            {
                displayPreviousMonth && (
                    <IconButton
                        {...NAV_BUTTON_PROPS}
                        hidden={!displayPreviousMonth}
                        aria-label="Previous month"
                        icon={<FaIcon icon={faAngleLeft} />}
                        isDisabled={previousMonth === undefined}
                        onClick={() => previousMonth !== undefined && goToMonth(previousMonth)}
                    />
                )
            }

            <Text fontWeight="bold">
                {format(displayMonth, 'MMM yyy')}
            </Text>

            {
                displayNextMonth && (
                    <IconButton
                        {...NAV_BUTTON_PROPS}
                        aria-label="Next month"
                        icon={<FaIcon icon={faAngleRight} />}
                        isDisabled={nextMonth === undefined}
                        onClick={() => nextMonth !== undefined && goToMonth(nextMonth)}
                    />
                )
            }
        </HStack>
    );
}

function getDateInputMatch(
    input: string,
    dateFormat: string,
    fromDate: Date | undefined,
    toDate: Date | undefined
) {
    if (!isMatch(input, dateFormat)
        || format(parse(input, dateFormat, new Date()), dateFormat) !== input) {
        return null;
    }

    const selectedDate = startOfDay(parse(input, dateFormat, new Date()));

    if (
        fromDate !== undefined
        && !isAfter(selectedDate, fromDate)
        && !isSameDay(selectedDate, fromDate)) {
        return null;
    }

    if (
        toDate !== undefined
        && !isBefore(selectedDate, toDate)
        && !isSameDay(selectedDate, toDate)
    ) {
        return null;
    }

    return selectedDate;
}

/**
 * Controlled Date Picker with dd/mm/yyyy manual input
 */
export const DateInputWithPicker = React.forwardRef<HTMLInputElement, DatePickerWithInputProps>(
    (props, ref) => {
        const {
            size = 'md',
            placeholder,
            noDateSelectedText = '',
            value,
            onChange = () => {},
            onBlur = () => {},
            isInvalid = false,
            allowClear = true,
            disabled = false,
            closeWhenSelectDate = true,
            showNoDateButton = false,
            showTodayButton = false,
            noDateButtonText = 'No date',
            'aria-label': ariaLabel,
            inputProps = {},
            ...dayPickerProps
        } = props;

        const popoverDisclosure = useDisclosure();
        const [dateInput, setDateInput] = useState<string>(() => {
            if (value === null || value === undefined) return '';
            if (value instanceof Date) return format(value, 'dd/MM/yyyy');
            return '';
        });

        // Sync controlled value (Date | null | string | InvalidDateInput sentinel)
        // into the text input. Intentionally ignore InvalidDateInput or
        // invalid string values to preserve in-progress user typing.
        useEffect(() => {
            const DISPLAY_FORMAT = 'dd/MM/yyyy';
            // Skip invalid sentinel to preserve user typing; no state sync
            if (value instanceof InvalidDateInput) return;

            // Null / undefined -> clear
            if (value == null) {
                setDateInput('');
                return;
            }

            // Date instance -> formatted
            if (value instanceof Date) {
                setDateInput(format(value, DISPLAY_FORMAT));
                return;
            }

            // Support canonical date-only string (yyyy-MM-dd) from persisted form / API
            if (typeof value === 'string') {
                const trimmed = value.trim();
                if (trimmed === '') {
                    setDateInput('');
                    return;
                }
                const parsed = parseISO(trimmed);
                if (isValid(parsed)) {
                    setDateInput(format(parsed, DISPLAY_FORMAT));
                }
            }
            // Any other object -> ignore
        }, [value]);

        // Default the selected month to be the value we've been provided if we have one
        const [selectedMonth, setSelectedMonth] = useState<Date | undefined>(() => (
            value instanceof Date ? value : undefined
        ));

        const onClear = () => {
            setDateInput('');
            onChange(null);
        };

        const onDateSelect = (selectedDate: Date | undefined) => {
        // If date picker has cleared the selected dates,
        // we will do the same
            if (selectedDate === undefined) {
                onClear();
                return;
            }

            if (selectedDate !== undefined) {
                setDateInput(format(selectedDate, 'dd/MM/yyyy'));
            } else {
                setDateInput('');
            }

            onChange(selectedDate);
            setSelectedMonth(selectedDate);
        };

        // Debounce input changes for smoother UX
        const debounceTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
        const handleDateInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            setDateInput(e.target.value);
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
            debounceTimeoutRef.current = setTimeout(() => {
                const input = e.target.value;
                let match = getDateInputMatch(input, 'dd/MM/yyyy', dayPickerProps.fromDate, dayPickerProps.toDate);
                if (!match) {
                    match = getDateInputMatch(input, 'd/M/yyyy', dayPickerProps.fromDate, dayPickerProps.toDate);
                }
                if (match) {
                    // Always format to dd/MM/yyyy
                    const formatted = format(match, 'dd/MM/yyyy');
                    setDateInput(formatted);
                    onChange(match);
                    setSelectedMonth(match);
                    return;
                }
                if (input.trim() === '') {
                    if (value !== null) onChange(null);
                    return;
                }
                // Emit stable invalid sentinel only
                // if previous value wasn't already invalid sentinel
                if (!(value instanceof InvalidDateInput)) {
                    onChange(INVALID_DATE_SENTINEL);
                }
            }, 300); // 300ms debounce
        };

        return (
            <Popover
                placement="bottom-start"
                returnFocusOnClose={false}
                isOpen={disabled ? false : popoverDisclosure.isOpen}
                onClose={() => {
                    popoverDisclosure.onClose();
                    onBlur();
                }}
                isLazy
            >
                {({ isOpen, onClose }) => (
                    <>
                        <PopoverTrigger>
                            <DateInput
                                ref={ref}
                                {...inputProps}
                                size={size}
                                placeholder={placeholder}
                                isPopoverOpen={isOpen}
                                selectedDate={value instanceof Date ? value : null}
                                isInvalid={isInvalid}
                                aria-label={ariaLabel}
                                noDateSelectedText={noDateSelectedText}
                                allowClear={allowClear}
                                disabled={disabled}
                                onClear={() => {
                                    onClear();
                                    onClose();
                                }}
                                onCalendarClick={() => popoverDisclosure.onToggle()}
                                inputValue={dateInput}
                                onInputValueChange={handleDateInputChange}
                            />
                        </PopoverTrigger>
                        <PopoverContent w="max-content">
                            <PopoverArrow />
                            <PopoverBody>
                                <VStack alignItems="stretch">
                                    <DayPicker
                                        {...dayPickerProps}
                                        mode="single"
                                        selected={value instanceof Date ? value : undefined}
                                        onSelect={(day) => {
                                            onDateSelect(day);

                                            if (closeWhenSelectDate) {
                                                onClose();
                                            }
                                        }}
                                        month={selectedMonth}
                                        onMonthChange={setSelectedMonth}
                                        components={{
                                            Caption: DatePickerCaption,
                                        }}
                                        required
                                    />
                                    {showNoDateButton && (
                                        <Button
                                            colorScheme="muted"
                                            size="sm"
                                            onClick={() => {
                                                onClear();

                                                if (closeWhenSelectDate) {
                                                    onClose();
                                                }
                                            }}
                                        >
                                            {noDateButtonText}
                                        </Button>
                                    )}
                                    {showTodayButton && (
                                        <Button
                                            colorScheme="muted"
                                            size="sm"
                                            onClick={() => {
                                                onDateSelect(startOfDay(new Date()));

                                                if (closeWhenSelectDate) {
                                                    onClose();
                                                }
                                            }}
                                        >
                                            Today
                                        </Button>
                                    )}
                                </VStack>
                            </PopoverBody>
                        </PopoverContent>
                    </>
                )}
            </Popover>
        );
    }
);
